import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Info } from 'lucide-react';
import { useIntegrationInstallationsQuery } from '@/Pages/Integrations/integrationsApi';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Dialog } from '@/components/ui/dialog';
import { Field, Select } from '@/components/ui/field';
import { Icon } from '@/components/ui/icon';
import { TD, TH, THead, TR, Table, TableWrap } from '@/components/ui/table';
import PanelResult from './PanelResult';
import { useAggregatorUnreconciledQuery } from './deliveryAggregatorsApi';
import type { IntegrationDelivery } from '@/Pages/Integrations/integrationsApi';
import { apiMessage, formatDateTime } from './lib';

/**
 * ADR-0028's own named gap: `AggregatorInboundListener` runs AFTER the
 * substrate's own dedupe already claimed the webhook, so a failure inside it
 * — an unmapped item, most often — is answered `duplicate: true` on the
 * provider's retry and never reaches this module again. Nothing auto-heals
 * this; `docs/delivery-aggregators.md` names this list as the operator's only
 * way to find and resolve it.
 *
 * **Read the scope printed below before trusting an empty list.** The query
 * behind this screen (`findUnlinkedDeliveries`) filters on
 * `eventName: 'order.created'` — the fixture connector's own event name for a
 * full order body. A connector whose real webhook is a thin "something
 * changed, go fetch it" pointer with a different event name (Uber Eats' own
 * `aggregator.notification_received`, read directly off
 * `uber-eats.connector.ts`'s `verifyWebhook`) does not match that filter even
 * when it produces nothing downstream — so this list can read as empty while
 * that class of gap is invisible to it. That is not a bug this screen can
 * paper over; it is named here so an empty result is never read as "nothing
 * is wrong."
 */
export default function UnreconciledTab() {
  const { t, i18n } = useTranslation();
  const [installationId, setInstallationId] = useState('');
  const [openPayload, setOpenPayload] = useState<IntegrationDelivery | null>(null);

  const { data: installationPage } = useIntegrationInstallationsQuery({ limit: 200, includeUninstalled: false });
  const deliveryInstallations = useMemo(
    () => (installationPage?.items ?? []).filter((i) => i.provider?.category === 'DELIVERY'),
    [installationPage],
  );

  const { data, isFetching, isError, error, refetch } = useAggregatorUnreconciledQuery(
    installationId || undefined,
  );
  const rows = data ?? [];

  return (
    <div className="space-y-4">
      <div
        className="flex items-start gap-2 rounded-lg border border-sky-300 bg-sky-50/60 p-3 text-sm dark:border-sky-900 dark:bg-sky-950/30"
        role="note"
      >
        <Icon as={Info} className="mt-0.5 shrink-0 text-sky-600 dark:text-sky-400" />
        <p className="text-ink-muted">{t('deliveryAggregators.unreconciled.scopeNote')}</p>
      </div>

      {deliveryInstallations.length > 0 && (
        <Field label={t('deliveryAggregators.mappings.installation')} controlId="unrec-installation">
          <Select value={installationId} onChange={(e) => setInstallationId(e.target.value)} className="w-72">
            <option value="">{t('deliveryAggregators.unreconciled.allInstallations')}</option>
            {deliveryInstallations.map((i) => (
              <option key={i.id} value={i.id}>
                {i.provider?.name ?? i.providerKey} — {i.branch?.name ?? t('deliveryAggregators.mappings.wholeVenue')}
              </option>
            ))}
          </Select>
        </Field>
      )}

      <Card>
        <CardContent className="p-0">
          <PanelResult
            loading={isFetching}
            error={isError ? apiMessage(error, t('deliveryAggregators.states.loadFailed')) : null}
            count={rows.length}
            onRetry={refetch}
            emptyTitle={t('deliveryAggregators.unreconciled.emptyTitle')}
            emptyHint={t('deliveryAggregators.unreconciled.emptyHint')}
          >
            <TableWrap>
              <Table>
                <THead>
                  <TR>
                    <TH>{t('deliveryAggregators.unreconciled.event')}</TH>
                    <TH>{t('deliveryAggregators.unreconciled.status')}</TH>
                    <TH>{t('deliveryAggregators.unreconciled.attempts')}</TH>
                    <TH>{t('deliveryAggregators.unreconciled.received')}</TH>
                    <TH />
                  </TR>
                </THead>
                <tbody>
                  {rows.map((row) => (
                    <TR key={row.id}>
                      <TD className="font-medium">{row.eventName}</TD>
                      <TD>
                        <Badge tone={row.status === 'DEAD_LETTERED' ? 'danger' : 'warning'}>
                          {t(`deliveryAggregators.unreconciled.deliveryStatus.${row.status}`)}
                        </Badge>
                      </TD>
                      <TD className="tabular-nums">{row.attempts}</TD>
                      <TD className="text-ink-muted">{formatDateTime(row.createdAt, i18n.language)}</TD>
                      <TD>
                        <Button variant="outline" size="sm" onClick={() => setOpenPayload(row)}>
                          {t('deliveryAggregators.unreconciled.viewPayload')}
                        </Button>
                      </TD>
                    </TR>
                  ))}
                </tbody>
              </Table>
            </TableWrap>
          </PanelResult>
        </CardContent>
      </Card>

      {openPayload && (
        <Dialog
          open
          onClose={() => setOpenPayload(null)}
          title={t('deliveryAggregators.unreconciled.payloadTitle')}
          description={openPayload.eventName}
          className="max-w-2xl"
          footer={
            <Button variant="secondary" onClick={() => setOpenPayload(null)} className="ms-auto">
              {t('common.close')}
            </Button>
          }
        >
          <pre className="scrollbar-thin max-h-[60vh] overflow-auto rounded-lg bg-subtle p-3 text-xs">
            {JSON.stringify(openPayload.payload, null, 2)}
          </pre>
        </Dialog>
      )}
    </div>
  );
}
